import React from 'react';
import { Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const relativeTime = (ms) => {
  if (!ms || ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return 'yesterday';
};

/**
 * Slim banner shown when the autosave hook finds an unfinished draft.
 *
 * @param {object} props
 * @param {boolean} props.show
 * @param {number}  props.draftAge   - ms since draft was saved
 * @param {() => void} props.onRestore
 * @param {() => void} props.onDismiss
 * @param {string} [props.testId]
 */
export const RestoreDraftBanner = ({ show, draftAge, onRestore, onDismiss, testId = 'restore-draft-banner' }) => {
  if (!show) return null;
  return (
    <div
      className="mb-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm"
      data-testid={testId}
    >
      <Save className="h-4 w-4 mt-0.5 shrink-0 text-blue-700" />
      <div className="flex-1 leading-relaxed text-blue-900">
        <span className="font-medium">We saved your last entry</span>
        <span className="text-blue-700"> ({relativeTime(draftAge)}). Want to pick up where you left off?</span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 border-blue-300 bg-white hover:bg-blue-100"
          onClick={onRestore}
          data-testid={`${testId}-restore`}
        >
          Restore
        </Button>
        <button
          type="button"
          aria-label="Dismiss"
          className="p-1 text-blue-500 hover:text-blue-700"
          onClick={onDismiss}
          data-testid={`${testId}-dismiss`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default RestoreDraftBanner;
